import { useEffect, useRef, useState, useCallback } from "react";
import { log } from "../lib/log";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Bell, Check, CheckCheck, MessageSquare, CalendarClock, AlertTriangle, ShoppingBag } from "lucide-react";

const ICONS = {
  task_assigned: CalendarClock,
  reminder_5d: CalendarClock,
  reminder_3d: CalendarClock,
  reminder_1d: CalendarClock,
  due_evening: AlertTriangle,
  overdue: AlertTriangle,
  chat_mention: MessageSquare,
  chat_message: MessageSquare,
  task_comment: MessageSquare,
  shopify_order: ShoppingBag,
  stock_alert: AlertTriangle,
  low_stock: AlertTriangle,
  sampling_digest: CalendarClock,
  production_late: AlertTriangle,
};

const TONE = {
  overdue: "text-red-600",
  stock_alert: "text-red-600",
  low_stock: "text-orange-500",
  production_late: "text-red-600",
  due_evening: "text-orange-400",
  task_assigned: "text-[#0A0A0A]",
};

const timeAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso).getTime();
  const diff = Date.now() - d;
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
};

export default function NotificationBell({ variantId = "" }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const nav = useNavigate();
  const ref = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch (err) {
      log.warn("[notifications] Failed to load notifications", err);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    const onNotif = () => load();
    window.addEventListener("atelier:notification", onNotif);
    return () => {
      clearInterval(t);
      window.removeEventListener("atelier:notification", onNotif);
    };
  }, [load]);

  useEffect(() => {
    const on = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", on);
    return () => document.removeEventListener("mousedown", on);
  }, []);

  const markAll = async () => {
    setBusy(true);
    try { await api.post("/notifications/mark-all-read"); await load(); }
    finally { setBusy(false); }
  };

  const openItem = async (n) => {
    if (!n.read) {
      try { await api.patch(`/notifications/${n.id}/read`); }
      catch (err) { log.warn("[notifications] Failed to mark read", err); }
    }
    setOpen(false);
    await load();
    if (n.link) nav(n.link);
    else if (n.task_id) nav("/tasks");
    else if (n.channel_id) nav("/chat");
  };

  const action = async (n, act) => {
    setError("");
    let comment;
    if (act === "add_comment") {
      comment = window.prompt("Add a comment:");
      if (!comment) return;
    }
    setBusy(true);
    try {
      await api.post(`/notifications/${n.id}/action`, { action: act, comment });
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid={variantId ? `notif-bell-${variantId}` : "notif-bell"}
        onClick={() => setOpen(!open)}
        className="relative w-9 h-9 flex items-center justify-center border border-[#E5E5E5] hover:border-[#0A0A0A] transition-colors"
        title="Notifications"
      >
        <Bell size={16} strokeWidth={1.5} className="text-[#525252]"/>
        {unread > 0 && (
          <span
            data-testid="notif-badge"
            className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-[#0A0A0A] text-white text-[10px] font-medium flex items-center justify-center"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notif-panel"
          className="absolute right-0 top-full mt-2 w-[380px] max-h-[520px] flex flex-col bg-[#FFFFFF] border border-[#E5E5E5] shadow-lg z-50"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E5]">
            <div className="label-caps text-[#0A0A0A]">Notifications</div>
            <button disabled={busy || unread === 0} onClick={markAll} className="text-xs text-[#0A0A0A] disabled:text-[#737373] inline-flex items-center gap-1" data-testid="notif-mark-all">
              <CheckCheck size={12}/> Mark all read
            </button>
          </div>
          <div className="overflow-y-auto flex-1">
            {items.length === 0 && (
              <div className="p-6 text-xs text-[#737373] italic text-center">No notifications yet.</div>
            )}
            {items.map(n => {
              const Icon = ICONS[n.type] || Bell;
              const tone = TONE[n.type] || "text-[#0A0A0A]";
              return (
                <div
                  key={n.id}
                  data-testid={`notif-item-${n.id}`}
                  className={`p-3 border-b border-[#E5E5E5] hover:bg-black/[0.03] cursor-pointer ${!n.read ? "bg-[#0A0A0A]/[0.03]" : ""}`}
                  onClick={() => openItem(n)}
                >
                  <div className="flex items-start gap-3">
                    <Icon size={14} strokeWidth={1.5} className={`${tone} mt-0.5`}/>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-xs font-medium text-[#0A0A0A] truncate">{n.title}</div>
                        <div className="text-[10px] text-[#737373] flex-shrink-0">{timeAgo(n.created_at)}</div>
                      </div>
                      {n.message && <div className="text-xs text-[#525252] mt-1 line-clamp-2">{n.message}</div>}
                      {(n.actions || []).length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2" onClick={(e)=>e.stopPropagation()}>
                          {n.actions.includes("mark_completed") && (
                            <button data-testid={`notif-act-complete-${n.id}`} onClick={() => action(n, "mark_completed")} className="text-[10px] uppercase tracking-widest text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1">
                              <Check size={10}/> Mark completed
                            </button>
                          )}
                          {n.actions.includes("still_pending") && (
                            <button onClick={() => action(n, "still_pending")} className="text-[10px] uppercase tracking-widest text-[#525252] hover:text-[#0A0A0A]">Still pending</button>
                          )}
                          {n.actions.includes("add_comment") && (
                            <button onClick={() => action(n, "add_comment")} className="text-[10px] uppercase tracking-widest text-[#0A0A0A] hover:text-[#0A0A0A]">+ Comment</button>
                          )}
                        </div>
                      )}
                      {!n.read && <div className="w-1.5 h-1.5 rounded-full bg-[#0A0A0A] mt-2"></div>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {error && <div className="p-2 text-xs text-red-600 border-t border-[#E5E5E5]">{error}</div>}
          <button onClick={()=>{setOpen(false); nav("/settings/notifications");}} className="text-[10px] uppercase tracking-widest text-[#525252] hover:text-[#0A0A0A] py-2 border-t border-[#E5E5E5]" data-testid="notif-open-prefs">
            Notification preferences
          </button>
        </div>
      )}
    </div>
  );
}
